import { useEffect, useRef, useState } from "react";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import useOnClickOutside from "../hooks/useOnClickOutside";
import { BiCalendar } from "react-icons/bi";
import "./calendar.scss";
import styles from "./Calendar.module.scss";
import {
  getIsoFormatStringWithTimeZone,
  getDateFromDateString,
} from "../../SharedModule/utils/dateUtils";

export const Calendar = ({
  date,
  className = "",
  isDisabled = false,
  isReadOnly = false,
  onChange: change = (date) => {},
  minDate = /** @type {Date | undefined} */ (undefined),
  maxDate = /** @type {Date | undefined} */ (undefined),
  initNull = false,
  tabIndex = 0,
  id = undefined,
}) => {
  const [value, onChange] = useState(initNull ? null : new Date());
  const [inputValue, setInputValue] = useState(initNull ? null : new Date());
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const calendarRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (focused) return;
    // ASUITE11-2621: Prop date could be a string or Date. When typeof is string should use getDateFromDateString to avoid change in timezone
    let dateProp;
    if (typeof date === "string") {
      dateProp = getDateFromDateString(date);
    } else {
      dateProp = date;
    }
    onChange(dateProp);
    if (dateProp) {
      setInputValue(
        dateProp.toLocaleString("en-us", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        }),
      );
    }
  }, [date, focused]);

  // Call hook passing in the ref and a function to call on outside click
  useOnClickOutside(calendarRef, (event) => {
    if (event.target !== inputRef.current) setVisible(false);
  });

  const onChangeCalendar = (date) => {
    setInputValue(
      date.toLocaleString("en-us", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      }),
    );
    onChange(date);
    change(getIsoFormatStringWithTimeZone(date));
    setVisible(false);
  };

  const onChangeInput = (event) => {
    const dateString = event.target.value;
    let newDate = dateString ? new Date(dateString) : null;
    setInputValue(dateString);
    if (dateString && Date.parse(dateString)) {
      newDate = new Date(dateString);
      onChange(newDate);
      change(getIsoFormatStringWithTimeZone(newDate));
    }
  };

  const focusHandler = () => {
    setVisible(true);
    setFocused(true);
  };

  const blurHandler = () => {
    setFocused(false);
  };

  return (
    <div className={`position-relative ${className}`}>
      <input
        id={id}
        className={`form-control form-control-sm calendar-input ${
          isReadOnly ? "common-input input-no-border" : ""
        } ${isDisabled ? "disabled" : ""}`}
        type="text"
        name="calendar"
        onFocus={focusHandler}
        onBlur={blurHandler}
        onChange={onChangeInput}
        value={inputValue}
        ref={inputRef}
        disabled={isDisabled}
        tabIndex={tabIndex}
      />
      <BiCalendar
        className={styles.icon}
        style={{ color: "#6357BB", width: "20px", height: "24px" }}
        onClick={() => inputRef.current.focus()}
      />

      {visible && (
        <ReactCalendar
          onChange={onChangeCalendar}
          inputRef={calendarRef}
          minDetail={"year"}
          showNeighboringMonth={false}
          value={value ?? null}
          locale="en-US"
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    </div>
  );
};
